import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC } from 'react'
import useTranslation from '@/hooks/useTranslation'
import { usePermissionAccess } from '@/features/auth/hooks/usePermissionAccess'

interface ETTableProps {
  rows: {
    ETzone: string
    soil: string
    vapour: string
    atomspheric: string
    speed: string
    number: string
    relative: string
    temperature: string
    flux: string
    shortwaver: string
    duration: string
    quantity: string
    irrigation_type: string
    vegetation: string
    type: string
    irrigation_time: string
  }[]
}

const ETTable: FC<ETTableProps> = ({ rows }) => {
  const { t } = useTranslation()
  const { isViewOnly } = usePermissionAccess()

  return (
    <Stack flexGrow="1">
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: 'common.white',
          border: '1px solid #EFEFEF',
          borderRadius: '3px',
          '&::-webkit-scrollbar': {
            width: '2px',
            height: '8px',
          },

          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#C1C1C1',
            borderRadius: '5px',
          },
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead sx={{ backgroundColor: '#EFEFEF' }}>
            <TableRow>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                  borderRight: '1px solid #D7D7D7',
                  backgroundColor: '#EFEFEF',
                  position: 'sticky',
                  left: 0,
                }}
              >
                {t('formsLabelsMicroClimate')}
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                {t('soilMoisture')}
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Vapour pressure derived from (RH)
              </TableCell>

              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Atmospheric pressure (kPa)
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Wind speed and height where it is collected (m/s)
              </TableCell>
              <TableCell
                sx={{
                  width: '121px',
                  height: '78px',
                }}
              >
                Number of hours (sunshine/sunlight) perday
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Relative humidity
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Dew point temperature (°C)
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Temperature (°C)
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Net soil heat flux (MH/m2)
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Incoming shortwaver radidation (MJ/m2)
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Duration of Irrigation
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Quantity (times per day)
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Irrigation Type
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                }}
              >
                Crop type - Vegetation
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                  borderRight: '1px solid #D7D7D7',
                }}
              >
                Irrigation Time
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '116.94px',
                  height: '78px',
                  backgroundColor: '#EFEFEF',
                  position: 'sticky',
                  right: 0,
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: 'common.white' }}>
            {rows.map((row) => (
              <TableRow key={row.ETzone}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    borderRight: '1px solid #D7D7D7',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  {row.ETzone}
                </TableCell>
                <TableCell>{row.soil}</TableCell>
                <TableCell>{row.vapour}</TableCell>
                <TableCell>{row.atomspheric}</TableCell>
                <TableCell>{row.speed}</TableCell>
                <TableCell>{row.number}</TableCell>
                <TableCell>{row.relative}</TableCell>
                <TableCell>{row.temperature}</TableCell>
                <TableCell>{row.flux}</TableCell>
                <TableCell>{row.shortwaver}</TableCell>
                <TableCell>{row.duration}</TableCell>
                <TableCell>{row.quantity}</TableCell>
                <TableCell>{row.irrigation_type}</TableCell>
                <TableCell>{row.vegetation}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell
                  sx={{
                    borderRight: '1px solid #D7D7D7',
                  }}
                >
                  {row.irrigation_time}
                </TableCell>
                <TableCell
                  sx={{
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Stack direction="row" spacing="10px">
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'primary.main',
                        opacity: isViewOnly ? 0.2 : 1,
                        pointerEvents: isViewOnly ? 'none' : 'auto',
                      }}
                    >
                      EDIT
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'grey.600',
                        opacity: isViewOnly ? 0.2 : 1,
                        pointerEvents: isViewOnly ? 'none' : 'auto',
                      }}
                    >
                      DELETE
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default ETTable
